import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum, getPriceTypeLabel, systemConfigKeyEnum, truckPriceTypeEnum } from '../../../../constants';
import { useCreateTruckMutation, useUpdateTruckMutation } from '../../../../hooks/mutations/useTruckMutation';
import { useAuth } from '../../../../hooks/useAuth';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { Truck } from '../../../../services/apiSLTP/materials/trucks';
import { TruckCompany } from '../../../../services/apiSLTP/materials/trucksCompanies';
import { TruckType } from '../../../../services/apiSLTP/materials/trucksTypes';
import { User } from '../../../../services/apiSLTP/users';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import CustomDateInput from '../../../ui/CustomDateInput';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown, { OptionType } from '../../../ui/DropDown';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody from '../../ModalBody';

interface ModalProps {
    truck: Truck | null;
    trucks: Truck[];
    trucksTypes: TruckType[];
    users: User[];
    trucksCompanies: TruckCompany[];
    showEditModal: boolean;
    isEditMode?: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateTruckModal: React.FC<ModalProps> = ({
    truck,
    showEditModal,
    setShowEditModal,
    trucksTypes,
    users,
    trucks,
    isEditMode = true,
    trucksCompanies,
    refetch,
}) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { getConfigValue } = useSystemConfig();

    const [selectedTruckType, setSelectedTruckType] = useState<OptionType | null>(null);
    const [selectedTruckCompany, setSelectedTruckCompany] = useState<OptionType | null>(null);
    const [selectedUser, setSelectedUser] = useState<OptionType | null>(null);

    const [selectedDesignation, setSelectedDesignation] = useState(truck?.designation || '');
    const [selectedCodeProC, setSelectedCodeProC] = useState(truck?.codeProC || '');
    const [selectedPlateNumber, setSelectedPlateNumber] = useState(truck?.plateNumber || '');
    const [selectedCrudStatus, setSelectedCrudStatus] = useState(truck?.crudStatus || crudStatusEnum.OK);

    const [price, setPrice] = useState(typeof truck?.price === 'number' ? truck?.price?.toString() : '');
    const [priceType, setPriceType] = useState<OptionType | null>(
        truck?.priceType
            ? {
                  label: capitalizeFirstLetter(t(getPriceTypeLabel(truck?.priceType))),
                  value: (truck?.priceType).toString(),
              }
            : null
    );
    const [model, setModel] = useState(truck?.model || '');
    const [baseName, setBaseName] = useState(truck?.baseName || '');
    const [startDate, setStartDate] = useState<Date | null>(truck?.startDate ? new Date(truck.startDate) : null);
    const [endDate, setEndDate] = useState<Date | null>(truck?.endDate ? new Date(truck.endDate) : null);

    const { mutate: mutateTruck } = useUpdateTruckMutation({
        onSuccess: refetch,
        onError: (error) => console.error('Error updating truck:', error.message),
    });

    const { mutate: addTruck } = useCreateTruckMutation({
        onSuccess: refetch,
        onError: (error) => console.error('Error adding truck:', error.message),
    });

    useEffect(() => {
        if (truck) {
            const truckType = trucksTypes.find((t) => t._id === truck.truckTypeId?._id);
            if (truckType) setSelectedTruckType({ label: truckType.designation, value: truckType._id });

            const truckCompany = trucksCompanies.find((c) => c._id === truck.truckCompanyId?._id);
            if (truckCompany) setSelectedTruckCompany({ label: truckCompany.designation, value: truckCompany._id });

            const user = users.find((u) => u._id === truck.userId?._id);
            if (user) setSelectedUser({ label: formatName(user.firstName, user.lastName), value: user._id });
        }
    }, [trucksTypes, trucksCompanies, users, truck]);

    const handleActiveChange = (isActive: boolean) => {
        setSelectedCrudStatus(isActive ? crudStatusEnum.OK : crudStatusEnum.INACTIVE);
    };

    const isFormValid = () => {
        return (
            selectedDesignation &&
            selectedCodeProC &&
            selectedPlateNumber &&
            selectedTruckCompany?.value &&
            selectedTruckType?.value &&
            baseName.length
        );
    };

    const handleSubmit = () => {
        if (!selectedTruckType || !selectedTruckCompany) return;

        if (isEditMode) {
            if (truck?._id)
                mutateTruck({
                    truckId: truck?._id,
                    data: {
                        designation: selectedDesignation,
                        codeProC: selectedCodeProC,
                        crudStatus: selectedCrudStatus,
                        truckTypeId: selectedTruckType.value,
                        plateNumber: selectedPlateNumber,
                        truckCompanyId: selectedTruckCompany.value,
                        userId: selectedUser?.value || null,
                        priceType: priceType ? parseInt(priceType.value) : null,
                        price: price ? parseFloat(price) : null,
                        model,
                        baseName,
                        startDate: startDate ? startDate.toISOString() : null,
                        endDate: endDate ? endDate.toISOString() : null,
                    },
                });
        } else {
            addTruck({
                designation: selectedDesignation,
                codeProC: selectedCodeProC,
                crudStatus: selectedCrudStatus,
                truckTypeId: selectedTruckType.value,
                plateNumber: selectedPlateNumber,
                truckCompanyId: selectedTruckCompany.value,
                userId: selectedUser?.value,
                priceType: priceType ? parseInt(priceType.value) : null,
                price: price ? parseFloat(price) : null,
                model,
                baseName,
                startDate: startDate ? startDate.toISOString() : null,
                endDate: endDate ? endDate.toISOString() : null,
            });
        }
    };

    return (
        <ModalBody
            title={t('truck')}
            subtitle={isEditMode ? t('update') : t('add')}
            disableSubmit={!isFormValid()}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            onSubmit={handleSubmit}
        >
            <CustomTextInput value={selectedDesignation} onChange={setSelectedDesignation} label={t('name')} required />
            <CustomTextInput value={selectedCodeProC} onChange={setSelectedCodeProC} label={t('codeProC')} required />
            <ToggleLine checked={selectedCrudStatus === crudStatusEnum.OK} onChange={handleActiveChange} title={capitalizeFirstLetter(t('active'))} />
            <CustomTextInput value={baseName} onChange={setBaseName} label={t('baseName')} required />
            <CustomTextInput value={selectedPlateNumber} onChange={setSelectedPlateNumber} label={t('plateNumber')} required />
            <DropDown
                items={users.map((user) => ({ label: formatName(user.firstName, user.lastName), value: user._id }))}
                selectedItem={selectedUser}
                handleChange={(e, value) => setSelectedUser(value)}
                inputLabel={t('driver')}
                disableClearable={false}
            />
            <DropDown
                items={trucksCompanies.map((company) => ({ label: company.designation, value: company._id }))}
                selectedItem={selectedTruckCompany}
                handleChange={(e, value) => setSelectedTruckCompany(value)}
                inputLabel={t('company')}
                required
            />
            <DropDown
                items={trucksTypes.map((type) => ({ label: type.designation, value: type._id }))}
                selectedItem={selectedTruckType}
                handleChange={(e, value) => setSelectedTruckType(value)}
                inputLabel={t('type')}
                required
            />
            <CustomTextInput
                value={price}
                onChange={setPrice}
                label={t('price')}
                errorText={t('error')}
                startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                type="number"
            />
            <DropDown
                items={Object.values(truckPriceTypeEnum).map((type) => ({
                    label: capitalizeFirstLetter(t(getPriceTypeLabel(type))),
                    value: type.toString(),
                }))}
                selectedItem={priceType}
                handleChange={(e, value) => {
                    setPriceType(value);
                }}
                inputLabel={t('pricing')}
                disableClearable={false}
            />
            <CustomDateInput
                label={t('startDate')}
                onChange={(value) => setStartDate(value ? new Date(value?.toISOString()) : null)}
                defaultDate={startDate}
            />
            <CustomDateInput
                label={t('endDate')}
                onChange={(value) => setEndDate(value ? new Date(value?.toISOString()) : null)}
                defaultDate={endDate}
            />

            <CustomTextInput value={model} onChange={setModel} label={t('model')} />
        </ModalBody>
    );
};

export default UpdateTruckModal;
